import type { ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useApiMutation } from '~/util/hooks/useApiMutation';

import { getRouteApi } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import {
    fullEditUserFormInput,
    fullEditUserFormSchema,
} from '~/util/forms/user';
import { updateUserMutation } from '~/util/mutations/staff/users';
import { useScopes } from '~/components/Providers/AuthProvider';

const Route = getRouteApi('/profile/user/$userid');

export const EditUserSheet = (modalProps: ModalProps) => {
    const profile = Route.useLoaderData({
        select: (data) => data.profile,
    });

    const { userid } = Route.useParams();
    const { hasScope } = useScopes();

    const userMutation = useApiMutation(updateUserMutation());

    const currentUser = useMemo(
        () => ({
            global_name: profile.global_name,
            description: profile.description,
            country: profile.country,
            ban_level: profile.ban_level,
            roles: profile.roles,
        }),
        [profile],
    );

    const userForm = useAppForm(
        formOptions({
            formId: 'edit-user-staff',
            defaultValues: currentUser as fullEditUserFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: fullEditUserFormSchema,
            },
            onSubmit: async ({ value }) => {
                userMutation.mutate({
                    data: value,
                    current: currentUser,
                    uuid: profile.id,
                    userid,
                });
            },
        }),
    );

    useEffect(() => {
        userForm.reset(currentUser);
    }, [userForm, currentUser]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit User</SheetTitle>
                        <SheetDescription>
                            Change profile details, ban level, and permissions.
                        </SheetDescription>
                    </SheetHeader>
                    <form
                        id="edit-user-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            userForm.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <userForm.AppField name="global_name">
                                {(field) => (
                                    <field.TextField
                                        label="Name"
                                        required
                                        placeholder="User name"
                                    />
                                )}
                            </userForm.AppField>
                            <userForm.AppField name="description">
                                {(field) => (
                                    <field.TextAreaField
                                        classname="w-lg"
                                        label="Description"
                                        placeholder="User description"
                                    />
                                )}
                            </userForm.AppField>
                            <userForm.AppField name="ban_level">
                                {(field) => (
                                    <field.BanLevelField
                                        label="Ban Level"
                                        required
                                        placeholder="Select Ban Level"
                                        forceDisabled={!hasScope('user_ban')}
                                    />
                                )}
                            </userForm.AppField>
                            <userForm.AppField name="country">
                                {(field) => (
                                    <field.CountryField
                                        label="Country"
                                        required
                                        placeholder="Select Country"
                                    />
                                )}
                            </userForm.AppField>
                            <userForm.AppField name="roles">
                                {(field) => (
                                    <field.RolesField label="Roles" required />
                                )}
                            </userForm.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <userForm.AppForm>
                                <userForm.SubscribeButton
                                    label="Edit user"
                                    classname="w-11/12"
                                />
                            </userForm.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};
